/* eslint-disable react-refresh/only-export-components -- context file exports provider + constants + hook */
import React, { createContext, useContext, useState, useEffect } from 'react';

// Supported countries.
// Entries tagged `region: 'us'` belong to the OPTRA US site (/us) only. They are never offered,
// detected, restored from localStorage or selectable on the Singapore site; see
// isSelectableCountry() and the `forcedCountry` prop on CountryProvider.
export const SUPPORTED_COUNTRIES = {
  SG: {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    currency: 'SGD',
    grantsAvailable: true
  },
  MY: {
    code: 'MY',
    name: 'Malaysia',
    flag: '🇲🇾',
    currency: 'MYR',
    grantsAvailable: true
  },
  ID: {
    code: 'ID',
    name: 'Indonesia',
    flag: '🇮🇩',
    currency: 'IDR',
    grantsAvailable: true
  },
  TH: {
    code: 'TH',
    name: 'Thailand',
    flag: '🇹🇭',
    currency: 'THB',
    grantsAvailable: true
  },
  VN: {
    code: 'VN',
    name: 'Vietnam',
    flag: '🇻🇳',
    currency: 'VND',
    grantsAvailable: true
  },
  PH: {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭',
    currency: 'PHP',
    grantsAvailable: true
  },
  BN: {
    code: 'BN',
    name: 'Brunei',
    flag: '🇧🇳',
    currency: 'BND',
    grantsAvailable: true
  },
  KH: {
    code: 'KH',
    name: 'Cambodia',
    flag: '🇰🇭',
    currency: 'KHR',
    grantsAvailable: true
  },
  LA: {
    code: 'LA',
    name: 'Laos',
    flag: '🇱🇦',
    currency: 'LAK',
    grantsAvailable: true
  },
  MM: {
    code: 'MM',
    name: 'Myanmar',
    flag: '🇲🇲',
    currency: 'MMK',
    grantsAvailable: true
  },
  US: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    grantsAvailable: true,
    region: 'us'
  }
};

// Default country
const DEFAULT_COUNTRY = 'SG';

/**
 * True for countries the Singapore site may detect, restore or select (the ASEAN list).
 * Countries that belong to another region's site (region: 'us') are excluded, so the
 * Singapore site behaves exactly as it did before the US entry existed.
 */
export const isSelectableCountry = (countryCode) => {
  const entry = SUPPORTED_COUNTRIES[countryCode];
  return Boolean(entry) && entry.region !== 'us';
};

/** The country list for the Singapore site's selector: SUPPORTED_COUNTRIES without other regions' entries. */
export const SELECTABLE_COUNTRIES = Object.fromEntries(
  Object.entries(SUPPORTED_COUNTRIES).filter(([code]) => isSelectableCountry(code))
);

const CountryContext = createContext({
  country: DEFAULT_COUNTRY,
  setCountry: () => {},
  countryInfo: SUPPORTED_COUNTRIES[DEFAULT_COUNTRY],
  isLoading: true
});

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

/**
 * Country Provider - Manages country selection and persistence
 * Features:
 * - localStorage persistence
 * - IP-based detection (optional)
 * - Country-specific content switching
 *
 * `forcedCountry` (used by the /us site: forcedCountry="US") pins the country for the whole tree:
 * nothing is read from or written to localStorage, timezone detection is skipped and
 * setCountry is a no-op. Without it the provider behaves exactly as it always has.
 */
export const CountryProvider = ({ children, forcedCountry = null }) => {
  const forced = forcedCountry && SUPPORTED_COUNTRIES[forcedCountry] ? forcedCountry : null;
  const [country, setCountryState] = useState(DEFAULT_COUNTRY);
  const [isLoading, setIsLoading] = useState(!forced);

  // Load country from localStorage on mount
  useEffect(() => {
    // A forced country never touches storage or detection.
    if (forced) {
      setIsLoading(false);
      return;
    }

    // SSR safety check - only run in browser
    if (typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    const savedCountry = localStorage.getItem('optra_country');
    if (savedCountry && isSelectableCountry(savedCountry)) {
      setCountryState(savedCountry);
    } else {
      // Try IP-based detection (optional - can be enhanced with actual IP geolocation)
      detectCountryFromIP();
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only, as before; `forced` is fixed per tree
  }, []);

  // Detect country from IP (simplified - can be enhanced with actual geolocation API)
  const detectCountryFromIP = async () => {
    // SSR safety check - only run in browser
    if (typeof window === 'undefined') return;

    try {
      // You can integrate with a geolocation service here
      // For now, we'll use browser timezone as a hint
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Simple timezone-based detection (not perfect, but better than nothing)
      if (timezone.includes('America') || timezone.includes('US') || timezone.includes('New_York') || timezone.includes('Los_Angeles')) {
        // 'US' belongs to the /us site, so this stays false here and American visitors of the
        // Singapore site keep Singapore content (the behaviour before the US entry existed).
        if (isSelectableCountry('US')) {
          setCountryState('US');
          localStorage.setItem('optra_country', 'US');
          return;
        }
      }
      
      // Default to Singapore for Asia-Pacific timezones
      if (timezone.includes('Singapore') || timezone.includes('Asia')) {
        setCountryState('SG');
        localStorage.setItem('optra_country', 'SG');
        return;
      }
    } catch (error) {
      console.warn('Country detection failed:', error);
    }
    
    // Fallback to default
    setCountryState(DEFAULT_COUNTRY);
    localStorage.setItem('optra_country', DEFAULT_COUNTRY);
  };

  // Set country and persist to localStorage
  const setCountry = (countryCode) => {
    if (forced) return;
    if (!isSelectableCountry(countryCode)) {
      console.warn(`Unsupported country code: ${countryCode}`);
      return;
    }
    
    setCountryState(countryCode);
    
    // SSR safety check - only persist in browser
    if (typeof window !== 'undefined') {
      localStorage.setItem('optra_country', countryCode);
      
      // Optional: Track country change for analytics
      if (window.trackPageView) {
        window.trackPageView(window.location.pathname, `Country: ${countryCode}`);
      }
    }
  };

  const activeCountry = forced || country;
  const countryInfo = SUPPORTED_COUNTRIES[activeCountry] || SUPPORTED_COUNTRIES[DEFAULT_COUNTRY];

  return (
    <CountryContext.Provider
      value={{
        country: activeCountry,
        setCountry,
        countryInfo,
        isLoading: forced ? false : isLoading,
        supportedCountries: forced ? { [forced]: SUPPORTED_COUNTRIES[forced] } : SELECTABLE_COUNTRIES
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};

export default CountryContext;

